<div
    class="agpr-viBrandIdentityCard agpr-viBrandIdentityCard-contextMenuParent"
    [class.agpr-viBrandIdentityCard--interactive]="interactive"
>
    <div class="agpr-viBrandIdentityCard-summary">
        @if (brand?.logo?.source && !logoLoadError) {
            <div class="agpr-viBrandIdentityCard-logo">
                <img
                    [src]="brand.logo.source"
                    [alt]="name"
                    (error)="logoLoadError = true"
                />
            </div>
        } @else {
            <div
                class="agpr-viBrandIdentityCard-logo"
                [style.background-color]="brand?.logoPlaceholderColor ?? defaultColor"
            >
                <span>{{ brand?.logoPlaceholder ?? placeholderText }}</span>
            </div>
        }
        <div
            [id]="id + '_name'"
            class="agpr-viBrandIdentityCard-name"
        >
            @if (isEditingName) {
                <agpr-text-input
                    [id]="id + '_nameInput'"
                    class="agpr-viBrandIdentityCard-nameInput"
                    [ariaLabel]="'Common.brandInformation.name.label'"
                    [placeholder]="'VisibilityIntelligence.brandSetupPanel.card.name.placeholder'"
                    [(ngModel)]="nameInputValue"
                    (blur)="doneEditingName()"
                    [bottomMargin]="false"
                ></agpr-text-input>
            } @else {
                <span
                    class="agpr-viBrandIdentityCard-nameText"
                    [agprTooltip]="name"
                    tooltipPosition="top"
                >
                    {{ name }}
                </span>
            }
            @if (!isEditingName) {
                <agpr-button
                    [id]="id + '_name_editButton'"
                    class="agpr-viBrandIdentityCard-name-editButton"
                    icon="fa-regular fa-duotone fa-pen"
                    [style]="buttonTypes.icon"
                    tooltip="VisibilityIntelligence.brandSetupPanel.card.name.edit"
                    tooltipPosition="top"
                    (click)="isEditingName = true; nameInputValue = name;"
                ></agpr-button>
            }
        </div>
    </div>
    @if (displayErrors && hasNameError) {
        <agpr-form-message [id]="id + '_name_error'" [marginBottom]="0">
            {{ 'Common.brandIdentity.emptyName.message' | localize }}
        </agpr-form-message>
    }
    @if (displayErrors && hasDuplicateNameError) {
        <agpr-form-message [id]="id + '_name_duplicateError'" [marginBottom]="0">
            {{ 'VisibilityIntelligence.brandSetupPanel.card.name.duplicate' | localize }}
        </agpr-form-message>
    }

    <div
        class="agpr-viBrandIdentityCard-content"
        [@cardState]="{value: isExpanded ? 'collapsed' : 'expanded'}"
    >
        @if (displayErrors) {
            @if (hasWebsitesError) {
                <agpr-form-message [id]="id + '_websites_error_collapsed'" [marginBottom]="0">
                    {{ 'VisibilityIntelligence.brandSetupPanel.card.websites.required' | localize }}
                </agpr-form-message>
            }
            @if (showExtendedFields) {
                @if (hasIndustryError) {
                    <agpr-form-message [id]="id + '_industry_error_collapsed'" [marginBottom]="0">
                        {{ 'VisibilityIntelligence.brandSetupPanel.card.industry.required' | localize }}
                    </agpr-form-message>
                }
                @if (hasBioError) {
                    <agpr-form-message [id]="id + '_bio_error_collapsed'" [marginBottom]="0">
                        {{ 'VisibilityIntelligence.brandSetupPanel.card.bio.required' | localize }}
                    </agpr-form-message>
                }
            }
        }
    </div>

    <div
        class="agpr-viBrandIdentityCard-content"
        [@cardState]="{value: isExpanded ? 'expanded' : 'collapsed'}"
    >
        <div class="agpr-viBrandIdentityCard-field">
            <div class="agpr-viBrandIdentityCard-brand">
                <agpr-autocomplete
                    [id]="id + '_brandAutocomplete'"
                    class="agpr-viBrandIdentityCard-brandAutocomplete"
                    [label]="'VisibilityIntelligence.brandSetupPanel.brandField.label'"
                    [placeholder]="'VisibilityIntelligence.brandSetupPanel.brandField.placeholder'"
                    [dropdown]="true"
                    [suggestions]="brandList$"
                    [customOptions]="brandAutocompleteCustomOptions"
                    [labelField]="'name'"
                    [ngModel]="brand"
                    (ngModelChange)="selectBrand($event)"
                    [forceSelection]="true"
                    [clearOnBlur]="false"
                    [disabled]="disabled"
                ></agpr-autocomplete>
                <agpr-button
                    [id]="id + '_manageBrandIdentity'"
                    [style]="buttonTypes.block"
                    icon="fa-regular fa-duotone fa-gear"
                    tooltip="VisibilityIntelligence.brandSetupPanel.manageButton"
                    (click)="manageBrandIdentity()"
                ></agpr-button>
            </div>
        </div>

        <div class="agpr-viBrandIdentityCard-field">
            <agpr-text-input-chips
                [id]="id + '_nameVariations'"
                [header]="'Common.brandInformation.nameVariations.label'"
                [placeholder]="'Common.brandInformation.nameVariations.placeholder'"
                [chipPlaceholder]="'Common.brandInformation.nameVariations.chipPlaceholder'"
                [splitOnPattern]="splitOnPattern"
                [alwaysShowChips]="true"
                [ngModel]="nameVariations"
                (ngModelChange)="nameVariationsChanged($event)"
                icon="fa-solid fa-circle-exclamation"
                [iconTooltip]="'VisibilityIntelligence.brandSetupPanel.card.nameVariations.tooltip'"
                [iconColor]="nameVariations?.length ? '#0D74BA' : '#C27612'"
                [disabled]="disabled"
            ></agpr-text-input-chips>
        </div>

        <div class="agpr-viBrandIdentityCard-field">
            <agpr-text-input-chips
                [id]="id + '_websites'"
                [header]="'Common.brandInformation.websites.label'"
                [placeholder]="'Common.brandInformation.websites.placeholder'"
                [chipPlaceholder]="'Common.brandInformation.websites.chipPlaceholder'"
                [splitOnPattern]="splitOnPattern"
                [useLink]="true"
                [chipValidations]="websiteChipValidations"
                [alwaysShowChips]="true"
                [ngModel]="websites"
                (ngModelChange)="websitesChanged($event)"
                icon="fa-solid fa-circle-exclamation"
                [iconTooltip]="'VisibilityIntelligence.brandSetupPanel.card.websites.tooltip'"
                [iconColor]="websites?.length ? '#0D74BA' : '#C27612'"
                [disabled]="disabled"
                [required]="true"
            ></agpr-text-input-chips>
            @if (displayErrors && hasWebsitesError) {
                <agpr-form-message [id]="id + '_websites_error'" [marginBottom]="0">
                    {{ 'VisibilityIntelligence.brandSetupPanel.card.websites.required' | localize }}
                </agpr-form-message>
            }
        </div>

        @if (showExtendedFields) {
            <div class="agpr-viBrandIdentityCard-field">
                <agpr-text-input
                    [id]="id + '_industry'"
                    [label]="'Common.brandInformation.industry.label'"
                    [placeholder]="'Common.brandInformation.industry.placeholder'"
                    [ngModel]="industry"
                    (ngModelChange)="industryChanged($event)"
                    [bottomMargin]="false"
                    [disabled]="disabled"
                    [required]="true"
                ></agpr-text-input>
                @if (displayErrors && hasIndustryError) {
                    <agpr-form-message [id]="id + '_industry_error'" [marginBottom]="0">
                        {{ 'VisibilityIntelligence.brandSetupPanel.card.industry.required' | localize }}
                    </agpr-form-message>
                }
            </div>

            <div class="agpr-viBrandIdentityCard-field">
                <agpr-textarea-input
                    [id]="id + '_bio'"
                    [label]="'Common.brandInformation.bio.label'"
                    [placeholder]="'Common.brandInformation.bio.placeholder'"
                    [rows]="5"
                    [ngModel]="bio"
                    (ngModelChange)="bioChanged($event)"
                    [bottomMargin]="false"
                    [disabled]="disabled"
                    [required]="true"
                ></agpr-textarea-input>

                @if (displayErrors && hasBioError) {
                    <agpr-form-message [id]="id + '_bio_error'" [marginBottom]="0">
                        {{ 'VisibilityIntelligence.brandSetupPanel.card.bio.required' | localize }}
                    </agpr-form-message>
                }
            </div>

            <div class="agpr-viBrandIdentityCard-field">
                <agpr-brand-custom-information
                    [id]="id + '_additionalInfo'"
                    class="agpr-viBrandIdentityCard-additionalInfo"
                    [config]="additionalInfoConfig"
                    [actions]="additionalInfoActions"
                    [ngModel]="additionalInfo"
                    (ngModelChange)="additionalInfoChanged($event)"
                    [disabled]="disabled"
                ></agpr-brand-custom-information>
            </div>
        }
    </div>

    @if (interactive) {
        <div class="agpr-viBrandIdentityCard-toggleButton">
            <agpr-button
                [id]="id + '_toggle'"
                [style]="buttonTypes.icon"
                [icon]="isExpanded ? 'fa-solid fa-angle-up' : 'fa-solid fa-angle-down'"
                [tooltip]="isExpanded ? 'Core.collapse' : 'Core.expand'"
                tooltipPosition="left"
                [initiallyFocusable]="false"
                (click)="isExpanded = !isExpanded"
            ></agpr-button>
        </div>
    }

    @if (removable) {
        <div class="agpr-contextMenu bottom right">
            <agpr-button
                [id]="id + '_remove'"
                [style]="buttonTypes.contextMenu"
                icon="fa-regular fa-duotone fa-square-xmark"
                tooltip="Core.button.remove"
                tooltipPosition="top"
                (click)="remove()"
            ></agpr-button>
        </div>
    }
</div>
